/**
 * TranslationHistory: 자막 히스토리 패널
 *
 * 마크업: translate_1.html `.chatbox--translation` 기반
 * - 시스템 메시지 (실시간 자막 시작/종료)
 * - 참여자별 자막 + 번역 텍스트
 * - 내 발언 (mychat 스타일)
 * - 번역중: 로딩 애니메이션
 * - 자동 스크롤
 */
import React, { useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import {
  LANGUAGE_FLAG_MAP,
  type TranslationLanguage,
  type SystemMessage,
} from '@/store/slices/translationSlice';
import type { CaptionCurrentText, CaptionEntry } from '@/store/slices/captionSlice';
import type { Participant } from '@/store/slices/participantsSlice';
import { DEFAULT_PROFILE, toAbsoluteProfileUrl, toDisplayProfileUrl } from '@/utils/profileUtils';
import VirtualChatList from '@/components/SVSideBar/VirtualChatList';
import TypewriterText, { InterimBubble, isFreshCaptionTimestamp } from '@/components/TypewriterText/TypewriterText';

/** 로딩 애니메이션 (3-dot) */
const TypingIndicator: React.FC = () => (
  <span className="rtcNew_chatProg">
    <span className="rtcNew_chatProgItem1" />
    <span className="rtcNew_chatProgItem2" />
    <span className="rtcNew_chatProgItem3" />
  </span>
);

const systemMessageText: Record<string, string> = {
  caption_started: '실시간 자막이 시작되었습니다.',
  caption_ended: '실시간 자막이 종료되었습니다.',
  translation_started: '실시간 번역이 시작되었습니다.',
  translation_ended: '실시간 번역이 종료되었습니다.',
};

/** 시스템 메시지 라인 */
const RecordLine: React.FC<{ message: SystemMessage }> = ({ message }) => (
  <div className="chatitem_recordLine">
    <span className="chatitem_recordLineText">
      {systemMessageText[message.type] ?? message.type}
    </span>
  </div>
);

/** 채팅 아이템 (자막 엔트리) */
const CaptionItem: React.FC<{
  entry: CaptionEntry;
  isMyChat: boolean;
  showTranslation: boolean;
  myLang: string;
  avatarUrl?: string;
  displayName: string;
  fontScale: number;
}> = ({ entry, isMyChat, showTranslation, myLang, avatarUrl, displayName, fontScale }) => {
  const flagSrc =
    entry.nationality && LANGUAGE_FLAG_MAP[entry.nationality as TranslationLanguage];

  // multiLangData가 있으면 현재 myLang에 맞는 번역문을 동적으로 선택
  let resolvedTranslation: string | null = null;
  if (showTranslation && entry.multiLangData) {
    const originLang = entry.multiLangData.origin || 'ko';
    if (myLang !== originLang && entry.multiLangData[myLang]) {
      resolvedTranslation = entry.multiLangData[myLang];
    }
  }
  if (!resolvedTranslation && showTranslation && entry.translatedText) {
    resolvedTranslation = entry.translatedText;
  }

  const hasTranslation = !!resolvedTranslation;
  // 방금 확정된 발화만 interim에서 이어서 타이핑. 과거 히스토리/스크롤백은 즉시 렌더
  const isFresh = isFreshCaptionTimestamp(entry.timestamp);

  return (
    <div className={`chatitem${isMyChat ? ' mychat' : ''}`}>
      {/* 프로필 — 내 발언이 아닐 때만 표시 */}
      {!isMyChat && (
        <div className="profile">
          <div className="profile__imgBox">
            {avatarUrl ? (
              <div className="common_coProfileBox">
                <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${avatarUrl})` }} />
              </div>
            ) : (
              <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd' }}>
                <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${DEFAULT_PROFILE})` }} />
              </div>
            )}
          </div>
          <div className="nameBox">
            <span className="name ellipsis">{displayName}</span>
            {flagSrc && (
              <img className="nameBox__icon" src={flagSrc} alt="국기이미지" />
            )}
          </div>
          {entry.isTranslating && (
            <span className="context_time">
              <TypingIndicator />
            </span>
          )}
        </div>
      )}

      {/* 내 발언이면서 번역 중일 때 */}
      {isMyChat && entry.isTranslating && !entry.isFinal && (
        <TypingIndicator />
      )}

      {/* 텍스트 영역 */}
      <div className={`context_new${hasTranslation ? ' context_new--trans' : ''}`}>
        <p className="context_text" style={fontScale !== 1 ? { fontSize: `${fontScale}em` } : undefined}>
          {!entry.isFinal && <TypingIndicator />}
          <TypewriterText text={entry.text} animKey={`trans:${entry.userId}`} enabled={isFresh} laneWrite={false} />
        </p>
        {hasTranslation && (
          <p className="context_transText" style={fontScale !== 1 ? { fontSize: `${fontScale}em` } : undefined}>
            <TypewriterText text={resolvedTranslation!} animKey={`trans-tr:${entry.userId}`} mode="append" enabled={isFresh} laneWrite={false} />
          </p>
        )}
      </div>
    </div>
  );
};

/** 진행 중 자막 아이템 */
const CurrentCaptionItem: React.FC<{
  current: CaptionCurrentText;
  isMyChat: boolean;
  showTranslation: boolean;
  myLang: string;
  avatarUrl?: string;
  displayName: string;
  fontScale: number;
}> = ({ current, isMyChat, showTranslation, myLang, avatarUrl, displayName, fontScale }) => {
  let interimTranslation: string | null = null;
  if (showTranslation && current.multiLangData) {
    const originLang = current.multiLangData.origin || 'ko';
    if (myLang !== originLang && current.multiLangData[myLang]) {
      interimTranslation = current.multiLangData[myLang];
    }
  }
  if (!interimTranslation && showTranslation && current.translatedText) {
    interimTranslation = current.translatedText;
  }

  const hasTranslation = !!interimTranslation;

  return (
    <div className={`chatitem${isMyChat ? ' mychat' : ''}`}>
      {!isMyChat && (
        <div className="profile">
          <div className="profile__imgBox">
            {avatarUrl ? (
              <div className="common_coProfileBox">
                <div
                  className="common_coProfileBox__imgBox"
                  style={{ backgroundImage: `url(${avatarUrl})` }}
                />
              </div>
            ) : (
              <div
                className="common_coProfileBox common_coProfileBox--icon"
                style={{ background: '#c1cbdd' }}
              >
                <div
                  className="common_coProfileBox__imgBox"
                  style={{ backgroundImage: `url(${DEFAULT_PROFILE})` }}
                />
              </div>
            )}
          </div>
          <div className="nameBox">
            <span className="name ellipsis">{displayName}</span>
          </div>
          <span className="context_time">
            <TypingIndicator />
          </span>
        </div>
      )}

      {isMyChat && (
        <TypingIndicator />
      )}

      <div className={`context_new${hasTranslation ? ' context_new--trans' : ''}`}>
        {/* partial 재작성으로 줄 수가 오르내리지 않도록 지금까지 도달한 높이를 유지 */}
        <InterimBubble className="context_text" style={fontScale !== 1 ? { fontSize: `${fontScale}em` } : undefined}>
          <TypewriterText text={current.text} animKey={`trans:${current.speakerId}`} />
        </InterimBubble>
        {hasTranslation && (
          <InterimBubble className="context_transText" style={fontScale !== 1 ? { fontSize: `${fontScale}em` } : undefined}>
            <TypewriterText text={interimTranslation!} animKey={`trans-tr:${current.speakerId}`} mode="append" />
          </InterimBubble>
        )}
      </div>
    </div>
  );
};

const TranslationHistory: React.FC = () => {
  const entries = useAppSelector((s) => s.caption.entries);
  const currentTexts = useAppSelector((s) => s.caption.currentTexts);
  const systemMessages = useAppSelector((s) => s.translation.systemMessages);
  const local = useAppSelector((s) => s.local);
  const isTranslateEnabled = useAppSelector((s) => s.translation.isTranslateEnabled);
  const fontSize = useAppSelector((s) => s.translation.fontSize);
  const myLang = useAppSelector((s) => s.translation.language);
  const participants = useAppSelector((s) => s.participants.participants);
  const currentCaptions = useMemo(
    () => Object.values(currentTexts).sort((a, b) => a.updatedAt - b.updatedAt),
    [currentTexts]
  );

  // 로컬 사용자 판별: jitsiId(원격 수신용)와 userId/portal_id(로컬 STT용) 모두 비교
  const isLocalEntry = (entry: CaptionEntry): boolean =>
    entry.userId === local.jitsiId || entry.userId === local.userId;

  /** 참여자 정보에서 avatarUrl과 displayName을 가져오는 헬퍼 */
  const getParticipantInfo = (entry: CaptionEntry): { avatarUrl?: string; displayName: string } => {
    if (isLocalEntry(entry)) {
      // local: userName = 실명, nickname = 닉네임
      const name = local.userName || local.nickname || local.userId;
      const nick = local.nickname || '';
      const displayName = name && nick && nick !== name
        ? `${name} (${nick})`
        : name;
      return { avatarUrl: toDisplayProfileUrl(local.profileUrl, local.avatarUrl), displayName };
    }

    const p: Participant | undefined = participants[entry.userId];
    if (p) {
      // participant: displayName = Jitsi 표시명(실명), nickname = 닉네임
      const name = p.displayName || p.nickname;
      const nick = p.nickname || '';
      const displayName = name && nick && nick !== name
        ? `${name} (${nick})`
        : name;
      return { avatarUrl: toDisplayProfileUrl(p.profileUrl, p.avatarUrl), displayName };
    }

    // 참여자를 찾을 수 없는 경우 (이미 퇴장한 참여자)
    const name = entry.userName || entry.nickname;
    const nick = entry.nickname || '';
    const displayName = name && nick && nick !== name
      ? `${name} (${nick})`
      : name;
    return { avatarUrl: toAbsoluteProfileUrl(entry.profileUrl), displayName };
  };

  type TimelineItem =
    | { type: 'system'; id: string; data: SystemMessage }
    | { type: 'caption'; id: string; data: CaptionEntry }
    | { type: 'current'; id: string; data: CaptionCurrentText };

  const getCurrentParticipantInfo = (current: CaptionCurrentText): { avatarUrl?: string; displayName: string } => {
    const isMe = current.speakerId === local.jitsiId || current.speakerId === local.userId;
    if (isMe) {
      const name = local.userName || local.nickname || local.userId;
      const nick = local.nickname || '';
      return {
        avatarUrl: toDisplayProfileUrl(local.profileUrl, local.avatarUrl),
        displayName: name && nick && nick !== name ? `${name} (${nick})` : name,
      };
    }

    const p: Participant | undefined = participants[current.speakerId];
    if (p) {
      const name = p.displayName || p.nickname;
      const nick = p.nickname || '';
      return {
        avatarUrl: toDisplayProfileUrl(p.profileUrl || current.profileUrl, p.avatarUrl),
        displayName: name && nick && nick !== name ? `${name} (${nick})` : name || current.speakerName,
      };
    }

    const name = current.speakerName || current.speakerId;
    const nick = current.nickname || '';
    return {
      avatarUrl: toAbsoluteProfileUrl(current.profileUrl),
      displayName: name && nick && nick !== name ? `${name} (${nick})` : name,
    };
  };

  const timeline = useMemo<TimelineItem[]>(() => {
    const next: TimelineItem[] = [];

    systemMessages.forEach((msg) => {
      next.push({ type: 'system', id: `system-${msg.id}`, data: msg });
    });
    entries.forEach((entry) => {
      // START_STT/END_STT 시스템 메시지는 대화기록(SpeechSideBar)에서만 표시
      if (entry.isSystem) return;
      next.push({ type: 'caption', id: `caption-${entry.id}`, data: entry });
    });

    next.sort((a, b) => {
      const tsA = a.type === 'current' ? a.data.updatedAt : a.data.timestamp;
      const tsB = b.type === 'current' ? b.data.updatedAt : b.data.timestamp;
      return tsA - tsB;
    });

    currentCaptions.forEach((current) => {
      next.push({ type: 'current', id: `current-${current.speakerId}`, data: current });
    });

    return next;
  }, [currentCaptions, entries, systemMessages]);

  const isLocalTimelineItem = (item: TimelineItem | undefined) => {
    if (!item) return false;
    if (item.type === 'caption') return isLocalEntry(item.data);
    if (item.type === 'current') return item.data.speakerId === local.jitsiId || item.data.speakerId === local.userId;
    return false;
  };

  const fontScale = fontSize / 100;

  const renderTimelineItem = (item: TimelineItem) => {
    if (item.type === 'system') {
      return <RecordLine message={item.data} />;
    }

    if (item.type === 'current') {
      const isMyChat = item.data.speakerId === local.jitsiId || item.data.speakerId === local.userId;
      const { avatarUrl, displayName } = getCurrentParticipantInfo(item.data);
      return (
        <CurrentCaptionItem
          current={item.data}
          isMyChat={isMyChat}
          showTranslation={isTranslateEnabled}
          myLang={myLang}
          avatarUrl={avatarUrl}
          displayName={displayName}
          fontScale={fontScale}
        />
      );
    }

    const entry = item.data;
    const isMyChat = isLocalEntry(entry);
    const { avatarUrl, displayName } = getParticipantInfo(entry);
    return (
      <CaptionItem
        entry={entry}
        isMyChat={isMyChat}
        showTranslation={isTranslateEnabled}
        myLang={myLang}
        avatarUrl={avatarUrl}
        displayName={displayName}
        fontScale={fontScale}
      />
    );
  };

  const estimateTimelineItemSize = (index: number) => {
    const item = timeline[index];
    if (!item) return 100;
    if (item.type === 'system') return 44;
    if (item.type === 'caption' && isLocalEntry(item.data)) return item.data.multiLangData || item.data.translatedText ? 116 : 84;
    return 112;
  };

  return (
    <VirtualChatList
      className="chatbox chatbox--translation"
      estimateSize={estimateTimelineItemSize}
      forceScrollToBottom={isLocalTimelineItem(timeline[timeline.length - 1])}
      getItemKey={(item) => item.id}
      items={timeline}
      renderItem={renderTimelineItem}
      rowGap={16}
    />
  );
};

export default TranslationHistory;
